import { useMemo } from 'react';
import { useStore } from '../../state/store';
import { Panel, Placeholder, panelStyle, tabularNums } from '../ui';
import { LogRow } from './LogRow';

const BUFFER_CAP = 400;

export function Logs() {
  const logs = useStore((s) => s.logs);
  const processes = useStore((s) => s.processes);
  const paused = useStore((s) => s.paused);
  const togglePause = useStore((s) => s.togglePause);
  const filter = useStore((s) => s.logFilter);
  const setFilter = useStore((s) => s.setLogFilter);

  /**
   * Chips are built from the processes that have actually written a line as
   * well as the live process list, so output from a process pm2 has since
   * dropped stays reachable in the buffer.
   */
  const names = useMemo(() => {
    const seen = new Set(processes.map((p) => p.name));
    for (const line of logs) seen.add(line.processName);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [processes, logs]);

  const feed = useMemo(() => {
    const filtered =
      filter === null ? logs : logs.filter((l) => l.processName === filter);
    return [...filtered].reverse();
  }, [logs, filter]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          ...panelStyle,
          padding: '16px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={togglePause}
          style={{
            padding: '8px 16px',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 600,
            background: paused ? 'var(--ink)' : 'var(--panel)',
            color: paused ? 'var(--panel)' : 'var(--ink)',
            border: `1px solid ${paused ? 'var(--ink)' : 'var(--line)'}`,
          }}
        >
          {paused ? 'Resume stream' : 'Pause stream'}
        </button>

        <div
          style={{
            width: 1,
            height: 24,
            background: 'var(--line)',
            margin: '0 6px',
          }}
        />

        {[null, ...names].map((name) => {
          const on = filter === name;
          return (
            <button
              key={name ?? '__all'}
              type="button"
              className={on ? undefined : 'hover-border'}
              onClick={() => setFilter(name)}
              style={{
                padding: '8px 14px',
                borderRadius: 999,
                fontSize: 13,
                background: on ? 'var(--ink)' : 'transparent',
                color: on ? 'var(--panel)' : 'var(--muted)',
                border: `1px solid ${on ? 'var(--ink)' : 'var(--line)'}`,
              }}
            >
              {name ?? 'All'}
            </button>
          );
        })}

        <div
          style={{
            marginLeft: 'auto',
            fontSize: 12.5,
            color: 'var(--faint)',
            ...tabularNums,
          }}
        >
          {logs.length} lines buffered · cap {BUFFER_CAP}
          {paused ? ' · paused' : ''}
        </div>
      </div>

      <Panel
        style={{
          padding: '8px 24px 12px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          // The buffer holds up to 400 lines. Left to the document flow that is
          // a page tens of thousands of pixels tall with the controls scrolled
          // off the top, so the feed scrolls inside the panel instead.
          maxHeight: 'calc(100vh - 210px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 16,
            padding: '14px 0 12px',
            borderBottom: '1px solid var(--line)',
            fontSize: 12,
            color: 'var(--faint)',
            flex: 'none',
          }}
        >
          <div style={{ width: 62, flex: 'none' }}>Time</div>
          <div style={{ width: 104, flex: 'none' }}>Process</div>
          <div style={{ width: 64, flex: 'none' }}>Level</div>
          <div style={{ flex: 1 }}>Message</div>
        </div>

        <div className="scroll-y" style={{ flex: 1, minHeight: 0 }}>
          {feed.length === 0 ? (
            <Placeholder
              title={filter ? `Nothing from ${filter}` : 'No output buffered'}
              detail={
                paused
                  ? 'The stream is paused. Resume it to start collecting lines again.'
                  : 'Lines appear here as processes write to stdout or stderr.'
              }
            />
          ) : (
            feed.map((line) => <LogRow key={line.key} line={line} />)
          )}
        </div>
      </Panel>
    </div>
  );
}
